// ProtectedRoutes.tsx

import { Outlet, Navigate, useParams, useLocation } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import { useAuthStore } from "@/store/useAuthStore";
import { SUPPORTED_LANGUAGES, type SupportedLanguage } from "@/types/Languages.types";

export const ProtectedRoute = () => {
  const { isAuthenticated, isLoading } = useAuthStore();
  const { lang } = useParams<{ lang?: string }>();
  const location = useLocation();

  // 1. Wait for Firebase initialization to complete before deciding auth status
  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          bgcolor: "background.default",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // 2. Compute dynamic localized login path
  const isValidLang = lang && SUPPORTED_LANGUAGES.includes(lang as SupportedLanguage);
  const targetLang = isValidLang ? lang : "zh-hant";
  const loginPath = `/${targetLang}/login`;

  // 3. Preserve target path in URL query param & location state for post-login redirect
  if (!isAuthenticated) {
    const currentPath = location.pathname + location.search;
    const redirectUrl = `${loginPath}?redirect=${encodeURIComponent(currentPath)}`;

    return <Navigate to={redirectUrl} state={{ from: location }} replace />;
  }

  // 4. Render protected child routes
  return <Outlet />;
};
